import { useEffect, useState, useCallback } from "react";
import api, { errMsg, fmtDate, downloadReport } from "../lib/api";
import { useI18n } from "../context/I18nContext";
import { useAuth } from "../context/AuthContext";
import { PageHeader, Empty } from "../components/common";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter, DialogTrigger } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { toast } from "sonner";
import { Plus, ArrowRight, CheckCircle2, X, FileSpreadsheet, FileText, ClipboardEdit, Printer } from "lucide-react";
import { useNavigate } from "react-router-dom";

const EMPTY = { type: "M1", equipment_id: "", priority: "med", symptom_code: "", cause_code: "", affected_system: "", description: "", mnt_type: "", pm_template_id: "", hourmeter_reading: 0 };

const AFFECTED_SYSTEMS = ["electrical","engine","transmission","hydraulic","pneumatic","brakes","structural","cooling","fuel","steering","cabin","other"];
const NOTIF_TYPES = ["M1","M2","M3","M4"];

const priorityStyle = (p) => ({
  low: "text-slate-600 bg-slate-100",
  med: "text-blue-700 bg-blue-100",
  high: "text-amber-700 bg-amber-100",
  critical: "text-red-700 bg-red-100",
}[p] || "text-slate-600 bg-slate-100");

const statusStyle = (s) => ({
  open: "text-amber-700 bg-amber-100 border-amber-300",
  reviewed: "text-blue-700 bg-blue-100 border-blue-300",
  converted: "text-emerald-700 bg-emerald-100 border-emerald-300",
  closed: "text-slate-600 bg-slate-200 border-slate-300",
}[s] || "text-slate-600 bg-slate-100 border-slate-300");

export default function MntNotifications() {
  const { t } = useI18n();
  const { can } = useAuth();
  const navigate = useNavigate();
  const [rows, setRows] = useState([]);
  const [equipment, setEquipment] = useState([]);
  const [symptoms, setSymptoms] = useState([]);
  const [causes, setCauses] = useState([]);
  const [pmTemplates, setPmTemplates] = useState([]);
  const [statusFilter, setStatusFilter] = useState("");
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const editable = can("admin", "maintenance", "warehouse");

  const load = useCallback(() => {
    api.get("/mnt/notifications", { params: statusFilter ? { status: statusFilter } : {} }).then((r) => setRows(r.data));
  }, [statusFilter]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    api.get("/equipment", { params: { active: true } }).then((r) => setEquipment(r.data));
    api.get("/mnt/failure-codes", { params: { category: "symptom" } }).then((r) => setSymptoms(r.data));
    api.get("/mnt/failure-codes", { params: { category: "cause" } }).then((r) => setCauses(r.data));
  }, []);

  // Load PM templates for the selected equipment when preventive is picked
  useEffect(() => {
    if (form.mnt_type === "preventive" && form.equipment_id) {
      api.get(`/mnt/pm-templates/for-equipment/${form.equipment_id}`)
        .then((r) => setPmTemplates(r.data))
        .catch(() => setPmTemplates([]));
    } else {
      setPmTemplates([]);
    }
  }, [form.mnt_type, form.equipment_id]);

  const selectedTemplate = pmTemplates.find((t) => t.id === form.pm_template_id);

  const save = async () => {
    try {
      await api.post("/mnt/notifications", form);
      toast.success(t("common.saved"));
      setOpen(false); setForm(EMPTY); load();
    } catch (e) { toast.error(errMsg(e)); }
  };

  const setStatus = async (id, st) => {
    try {
      await api.post(`/mnt/notifications/${id}/status`, { status: st });
      toast.success(t("common.saved"));
      load();
    } catch (e) { toast.error(errMsg(e)); }
  };

  const convertToWO = (n) => navigate(`/mnt/work-orders?fromNotif=${n.id}`);
  const openInternalOrder = (n) => navigate(`/mnt/equipment/${n.equipment_id}/internal-order`);

  const exportFile = async (fmt) => {
    try {
      const params = statusFilter ? `?status=${statusFilter}&format=${fmt}` : `?format=${fmt}`;
      await downloadReport(`/reports/mnt/notifications${params}`, `avisos_mantenimiento.${fmt === "xlsx" ? "xlsx" : fmt}`);
    } catch (e) { toast.error(errMsg(e)); }
  };

  return (
    <div data-testid="mnt-notifications-page">
      <PageHeader title={t("mnt.n.title")} subtitle={t("mnt.n.subtitle")}>
        <div className="flex gap-2 flex-wrap">
          <Select value={statusFilter || "all"} onValueChange={(v) => setStatusFilter(v === "all" ? "" : v)}>
            <SelectTrigger className="rounded-none h-8 text-xs w-40" data-testid="notif-status-filter">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">— {t("common.all") || "Todos"} —</SelectItem>
              {["open","reviewed","converted","closed"].map((s) =>
                <SelectItem key={s} value={s}>{t(`mnt.n.status.${s}`)}</SelectItem>)}
            </SelectContent>
          </Select>
          <div className="flex border" data-testid="notif-export-group">
            <button onClick={() => exportFile("csv")} className="border-r px-2 h-8 text-[10px] font-bold hover:bg-muted flex items-center gap-1" data-testid="notif-export-csv" title="CSV">
              <FileText size={12} /> CSV
            </button>
            <button onClick={() => exportFile("xlsx")} className="px-2 h-8 text-[10px] font-bold hover:bg-muted flex items-center gap-1" data-testid="notif-export-xlsx" title="Excel">
              <FileSpreadsheet size={12} /> XLSX
            </button>
          </div>
          {editable && (
            <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (!o) setForm(EMPTY); }}>
              <DialogTrigger asChild>
                <Button size="sm" className="rounded-none text-xs font-bold" data-testid="notif-new-button">
                  <Plus size={13} className="mr-1" /> {t("mnt.n.new")}
                </Button>
              </DialogTrigger>
              <DialogContent className="rounded-none sm:max-w-lg">
                <DialogHeader>
                  <DialogTitle className="uppercase font-black">{t("mnt.n.new")}</DialogTitle>
                  <DialogDescription className="text-xs">{t("mnt.n.subtitle")}</DialogDescription>
                </DialogHeader>
                <div className="space-y-3">
                  <div className="grid grid-cols-2 gap-3">
                    <div className="space-y-1">
                      <Label className="text-[10px] font-bold uppercase tracking-widest">{t("mnt.n.type")}</Label>
                      <Select value={form.type} onValueChange={(v) => setForm({ ...form, type: v })}>
                        <SelectTrigger className="rounded-none h-8 text-xs" data-testid="notif-form-type"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          {NOTIF_TYPES.map((tp) => <SelectItem key={tp} value={tp}>{t(`mnt.n.type.${tp}`)}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-1">
                      <Label className="text-[10px] font-bold uppercase tracking-widest">{t("mnt.priority")}</Label>
                      <Select value={form.priority} onValueChange={(v) => setForm({ ...form, priority: v })}>
                        <SelectTrigger className="rounded-none h-8 text-xs" data-testid="notif-form-priority"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          {["low","med","high","critical"].map((p) => <SelectItem key={p} value={p}>{t(`mnt.p.${p}`)}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  {/* Tipo de mantenimiento */}
                  <div className="grid grid-cols-2 gap-3">
                    <div className="space-y-1">
                      <Label className="text-[10px] font-bold uppercase tracking-widest">Tipo de mantenimiento</Label>
                      <Select value={form.mnt_type || "none"} onValueChange={(v) => setForm({ ...form, mnt_type: v === "none" ? "" : v, pm_template_id: "" })}>
                        <SelectTrigger className="rounded-none h-8 text-xs" data-testid="notif-form-mnttype">
                          <SelectValue placeholder="—" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="none">—</SelectItem>
                          <SelectItem value="preventive">Preventivo</SelectItem>
                          <SelectItem value="corrective">Correctivo</SelectItem>
                          <SelectItem value="emergency">Emergencia</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-1">
                      <Label className="text-[10px] font-bold uppercase tracking-widest">Horómetro (h)</Label>
                      <Input type="number" step="1" min="0"
                             value={form.hourmeter_reading || ""}
                             onChange={(e) => setForm({ ...form, hourmeter_reading: Number(e.target.value) })}
                             className="rounded-none h-8 text-xs" data-testid="notif-form-hourmeter" />
                    </div>
                  </div>
                  {form.mnt_type === "preventive" && (
                    <div className="space-y-1 border p-2 bg-primary/5">
                      <Label className="text-[10px] font-bold uppercase tracking-widest">Servicio PM programado</Label>
                      <Select value={form.pm_template_id || "none"}
                              onValueChange={(v) => setForm({ ...form, pm_template_id: v === "none" ? "" : v })}
                              disabled={!form.equipment_id}>
                        <SelectTrigger className="rounded-none h-8 text-xs" data-testid="notif-form-pmtemplate">
                          <SelectValue placeholder={form.equipment_id ? "Elige un servicio…" : "Selecciona primero un equipo"} />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="none">—</SelectItem>
                          {pmTemplates.map((tpl) => (
                            <SelectItem key={tpl.id} value={tpl.id}>
                              {tpl.code} · {tpl.name} · {tpl.interval_hours} h
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      {selectedTemplate && (
                        <div className="mt-2 border bg-card p-2 text-[11px]" data-testid="pm-template-preview">
                          <div className="font-bold text-primary mb-1">{selectedTemplate.code} · {selectedTemplate.name}</div>
                          <div className="grid grid-cols-3 gap-2 text-[10px]">
                            <div>
                              <div className="font-bold uppercase tracking-widest text-muted-foreground">Filtros ({selectedTemplate.filters?.length || 0})</div>
                              <ul className="mt-0.5">
                                {(selectedTemplate.filters || []).map((f, i) => (
                                  <li key={i} className="truncate">· {f.item_number} × {f.qty}</li>
                                ))}
                              </ul>
                            </div>
                            <div>
                              <div className="font-bold uppercase tracking-widest text-muted-foreground">Aceites ({selectedTemplate.oils?.length || 0})</div>
                              <ul className="mt-0.5">
                                {(selectedTemplate.oils || []).map((o, i) => (
                                  <li key={i} className="truncate">· {o.item_number} × {o.qty} {o.uom}</li>
                                ))}
                              </ul>
                            </div>
                            <div>
                              <div className="font-bold uppercase tracking-widest text-muted-foreground">Tareas ({selectedTemplate.tasks?.length || 0})</div>
                              <ul className="mt-0.5">
                                {(selectedTemplate.tasks || []).slice(0, 4).map((task, i) => (
                                  <li key={i} className="truncate">· {task.description}</li>
                                ))}
                                {selectedTemplate.tasks?.length > 4 && (
                                  <li className="text-muted-foreground">…y {selectedTemplate.tasks.length - 4} más</li>
                                )}
                              </ul>
                            </div>
                          </div>
                          <div className="text-[9px] text-muted-foreground mt-1.5">
                            Al guardar se reservará el stock de filtros y aceites.
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                  <div className="space-y-1">
                    <Label className="text-[10px] font-bold uppercase tracking-widest">{t("mnt.equipment")}</Label>
                    <Select value={form.equipment_id || "none"} onValueChange={(v) => setForm({ ...form, equipment_id: v === "none" ? "" : v })}>
                      <SelectTrigger className="rounded-none h-8 text-xs" data-testid="notif-form-equipment">
                        <SelectValue placeholder={t("mnt.selectEquipment")} />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">—</SelectItem>
                        {equipment.map((e) =>
                          <SelectItem key={e.id} value={e.id}>
                            {e.code} · {e.name}{e.model ? ` · ${e.model}` : ""}
                          </SelectItem>)}
                      </SelectContent>
                    </Select>
                    <div className="text-[9px] text-muted-foreground">N° Económico · Descripción · Modelo</div>
                  </div>
                  <div className="space-y-1">
                    <Label className="text-[10px] font-bold uppercase tracking-widest">{t("mnt.n.affectedSystem")}</Label>
                    <Select value={form.affected_system || "none"} onValueChange={(v) => setForm({ ...form, affected_system: v === "none" ? "" : v })}>
                      <SelectTrigger className="rounded-none h-8 text-xs" data-testid="notif-form-affected"><SelectValue placeholder="—" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">—</SelectItem>
                        {AFFECTED_SYSTEMS.map((s) => <SelectItem key={s} value={s}>{t(`mnt.as.${s}`)}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div className="space-y-1">
                      <Label className="text-[10px] font-bold uppercase tracking-widest">{t("mnt.n.symptom")}</Label>
                      <Select value={form.symptom_code || "none"} onValueChange={(v) => setForm({ ...form, symptom_code: v === "none" ? "" : v })}>
                        <SelectTrigger className="rounded-none h-8 text-xs" data-testid="notif-form-symptom"><SelectValue placeholder="—" /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="none">—</SelectItem>
                          {symptoms.map((s) => <SelectItem key={s.id} value={s.code}>{s.code} · {s.name}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-1">
                      <Label className="text-[10px] font-bold uppercase tracking-widest">{t("mnt.n.cause")}</Label>
                      <Select value={form.cause_code || "none"} onValueChange={(v) => setForm({ ...form, cause_code: v === "none" ? "" : v })}>
                        <SelectTrigger className="rounded-none h-8 text-xs" data-testid="notif-form-cause"><SelectValue placeholder="—" /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="none">—</SelectItem>
                          {causes.map((c) => <SelectItem key={c.id} value={c.code}>{c.code} · {c.name}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  <div className="space-y-1">
                    <Label className="text-[10px] font-bold uppercase tracking-widest">{t("mnt.description")}</Label>
                    <Textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })}
                      rows={3} className="rounded-none text-xs" data-testid="notif-form-description" />
                  </div>
                </div>
                <DialogFooter>
                  <Button onClick={save} disabled={!form.equipment_id || !form.description}
                    className="rounded-none font-bold text-xs" data-testid="notif-save-button">
                    {t("common.save")}
                  </Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          )}
        </div>
      </PageHeader>

      {rows.length === 0 ? <Empty text={t("common.noData")} /> : (
        <div className="tbl-wrap" data-testid="notif-table">
          <table className="tbl">
            <thead><tr>
              <th>{t("mnt.n.number")}</th><th>{t("mnt.n.type")}</th>
              <th>N° Eco.</th><th>{t("mnt.equipment")}</th><th>{t("eq.model")}</th>
              <th>{t("mnt.n.affectedSystem")}</th>
              <th>{t("mnt.priority")}</th><th>{t("mnt.n.symptom")}</th><th>{t("mnt.description")}</th>
              <th>{t("mnt.reportedBy")}</th><th>{t("mnt.n.createdAt")}</th><th>{t("common.status")}</th><th></th>
            </tr></thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.id} data-testid={`notif-row-${r.notif_number}`}>
                  <td className="font-bold tabular-nums">{r.notif_number}</td>
                  <td className="font-bold text-[10px]">{r.type}</td>
                  <td className="text-[11px] font-bold tabular-nums">{r.equipment_code}</td>
                  <td className="text-[11px]">{r.equipment_name}</td>
                  <td className="text-[10px] text-muted-foreground">{r.equipment_model || "—"}</td>
                  <td className="text-[10px]">{r.affected_system ? t(`mnt.as.${r.affected_system}`) : "—"}</td>
                  <td><span className={`px-1.5 py-0.5 text-[10px] font-bold uppercase ${priorityStyle(r.priority)}`}>{t(`mnt.p.${r.priority}`)}</span></td>
                  <td className="text-[11px]">{r.symptom_code || "—"}</td>
                  <td className="text-[11px] max-w-64 truncate">{r.description}</td>
                  <td className="text-[10px] text-muted-foreground">{r.reported_by}</td>
                  <td className="text-[10px] text-muted-foreground tabular-nums">{fmtDate(r.created_at || r.reported_at)}</td>
                  <td><span className={`inline-block border px-1.5 py-0.5 text-[10px] font-bold uppercase ${statusStyle(r.status)}`}>{t(`mnt.n.status.${r.status}`)}</span></td>
                  <td>
                    <div className="flex gap-1 items-center">
                      <button onClick={() => openInternalOrder(r)} className="text-primary hover:opacity-70" title={t("eq.viewInternalOrder")} data-testid={`notif-internal-${r.notif_number}`}>
                        <ClipboardEdit size={13} />
                      </button>
                      {r.mnt_type === "preventive" && r.pm_snapshot && (
                        <button onClick={() => {
                          const url = `${process.env.REACT_APP_BACKEND_URL}/api/mnt/notifications/${r.id}/gama-pdf`;
                          const w = window.open(url, "_blank");
                          if (w) w.addEventListener("load", () => { try { w.focus(); w.print(); } catch (e) {} });
                        }} className="text-primary hover:opacity-70" title="Imprimir gama"
                                data-testid={`notif-gama-${r.notif_number}`}>
                          <Printer size={13} />
                        </button>
                      )}
                      {editable && r.status === "open" && (
                        <>
                          <button onClick={() => setStatus(r.id, "reviewed")} className="text-blue-600 hover:opacity-70" title={t("mnt.n.markReviewed")} data-testid={`notif-review-${r.notif_number}`}>
                            <CheckCircle2 size={13} />
                          </button>
                          <button onClick={() => convertToWO(r)} className="text-emerald-600 hover:opacity-70" title={t("mnt.n.convert")} data-testid={`notif-convert-${r.notif_number}`}>
                            <ArrowRight size={13} />
                          </button>
                          <button onClick={() => setStatus(r.id, "closed")} className="text-red-600 hover:opacity-70" title={t("mnt.n.closeNotif")} data-testid={`notif-close-${r.notif_number}`}>
                            <X size={13} />
                          </button>
                        </>
                      )}
                      {editable && r.status === "reviewed" && (
                        <>
                          <button onClick={() => convertToWO(r)} className="text-emerald-600 hover:opacity-70 flex items-center gap-1 text-[10px] font-bold" data-testid={`notif-convert-${r.notif_number}`}>
                            <ArrowRight size={12} /> OT
                          </button>
                          <button onClick={() => setStatus(r.id, "closed")} className="text-red-600 hover:opacity-70" title={t("mnt.n.closeNotif")} data-testid={`notif-close-${r.notif_number}`}>
                            <X size={13} />
                          </button>
                        </>
                      )}
                      {r.wo_id && (
                        <button onClick={() => navigate(`/mnt/work-orders?open=${r.wo_id}`)} className="text-emerald-600 hover:opacity-70 text-[10px] font-bold" data-testid={`notif-linked-${r.notif_number}`}>
                          ↗ OT
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
